import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { kasaAPI, masrafAPI } from '../api/api';
import { useAuth } from '../context/AuthContext';
import { getTodayDateString, formatDateOnly, toISODateOrNull } from '../utils/dateUtils';
import { canAccessOzelDosyalar } from '../utils/ozelDosyaAccess';
import KdvPaneli from '../components/Kasa/KdvPaneli';
import './Kasa.css';
import { onayla } from '../utils/onayla';

const AYLAR = [
  { value: 1, label: 'Ocak' }, { value: 2, label: 'Şubat' }, { value: 3, label: 'Mart' },
  { value: 4, label: 'Nisan' }, { value: 5, label: 'Mayıs' }, { value: 6, label: 'Haziran' },
  { value: 7, label: 'Temmuz' }, { value: 8, label: 'Ağustos' }, { value: 9, label: 'Eylül' },
  { value: 10, label: 'Ekim' }, { value: 11, label: 'Kasım' }, { value: 12, label: 'Aralık' },
];

const paraFormat = (tutar) =>
  new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY', minimumFractionDigits: 2 })
    .format(Number(tutar) || 0);

/** Hem PascalCase hem camelCase alan adlarını okur (API ikisini de gönderebiliyor). */
const al = (nesne, ad, varsayilan = undefined) => {
  if (!nesne) return varsayilan;
  const kucuk = ad.charAt(0).toLowerCase() + ad.slice(1);
  return nesne[ad] ?? nesne[kucuk] ?? varsayilan;
};

const Kasa = () => {
  const { user } = useAuth();
  const [kasaData, setKasaData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  // Derin baglanti: /kasa?sekme=kisisel dogrudan kisisel gider ekranini acar.
  // iOS kisayolu ya da ana ekran widget'i buraya baglanabilir.
  const [aramaParam, setAramaParam] = useSearchParams();
  const [sekme, setSekmeDurumu] = useState(aramaParam.get('sekme') || 'dosya');

  const setSekme = useCallback((yeni) => {
    setSekmeDurumu(yeni);
    setAramaParam(yeni === 'dosya' ? {} : { sekme: yeni }, { replace: true });
  }, [setAramaParam]);
  const [dosyaMasrafTab, setDosyaMasrafTab] = useState('gelir');
  // Gider dağılımında hangi kalemin dökümü açık: 'muvekkile' | 'dosya' |
  // 'referans' | 'diger' | 'beril' | null
  const [acikDokum, setAcikDokum] = useState(null);
  const [formData, setFormData] = useState({
    IsGelir: true,
    Tutar: '',
    MasrafIstenmeTarihi: getTodayDateString(),
    SonTarih: '',
    Aciklama: '',
    MasrafTuru: '',
    IsBerileVerilen: false,
  });

  const userId = user?.Id ?? user?.id;
  const kasayaErisebilir = userId === 2 || userId === 8;
  // Kişisel giderler yalnızca Baran'a açık — özel dosyalarla aynı kural.
  const kisiselErisim = canAccessOzelDosyalar(user);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setKasaData(await kasaAPI.getSummary(selectedYear, selectedMonth));
    } catch (error) {
      alert('Kasa verisi yüklenirken hata oluştu: ' + (error.response?.data?.message || error.message));
    } finally {
      setLoading(false);
    }
  }, [selectedYear, selectedMonth]);

  useEffect(() => {
    if (!kasayaErisebilir) return;
    loadData();
  }, [kasayaErisebilir, loadData]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await kasaAPI.createMasraf({
        IsGelir: formData.IsGelir,
        Tutar: parseFloat(formData.Tutar),
        Tarih: toISODateOrNull(formData.MasrafIstenmeTarihi),
        MasrafIstenmeTarihi: toISODateOrNull(formData.MasrafIstenmeTarihi),
        SonTarih: toISODateOrNull(formData.SonTarih),
        Aciklama: formData.Aciklama,
        MasrafTuru: formData.MasrafTuru,
        IsBerileVerilen: formData.IsBerileVerilen && !formData.IsGelir,
      });
      setShowForm(false);
      setFormData({
        IsGelir: true, Tutar: '', MasrafIstenmeTarihi: getTodayDateString(),
        SonTarih: '', Aciklama: '', MasrafTuru: '', IsBerileVerilen: false,
      });
      loadData();
    } catch (error) {
      alert('Kayıt eklenirken hata oluştu: ' + (error.response?.data?.message || error.message));
    }
  };

  const handleDelete = async (id) => {
    if (!await onayla('Bu kayıt silinsin mi?')) return;
    try {
      await masrafAPI.delete(id);
      loadData();
    } catch (error) {
      alert('Silinirken hata oluştu: ' + (error.response?.data?.message || error.message));
    }
  };

  const formatDate = (d) => (d ? formatDateOnly(d) : '-');

  /**
   * Ödeme satırlarını dosyaya göre toplar: "hangi dosyadan ne kadar".
   * Dosyaya bağlı olmayan kayıtlar tek bir "Dosyasız" satırında birleşir.
   * Büyükten küçüğe sıralanır — önce en çok ödeme yapılan dosya görünür.
   */
  const dosyayaGoreTopla = (satirlar) => {
    const gruplar = new Map();
    for (const r of satirlar) {
      const dosyaId = r.caseFileId ?? 'yok';
      const mevcut = gruplar.get(dosyaId);
      if (mevcut) {
        mevcut.tutar += r.tutar;
        mevcut.adet += 1;
        if (r.tarih && (!mevcut.sonTarih || new Date(r.tarih) > new Date(mevcut.sonTarih))) {
          mevcut.sonTarih = r.tarih;
        }
      } else {
        gruplar.set(dosyaId, {
          anahtar: String(dosyaId),
          caseFileId: r.caseFileId ?? null,
          foyNo: r.foyNo ?? null,
          muvekkil: r.muvekkil ?? null,
          tutar: r.tutar,
          adet: 1,
          sonTarih: r.tarih ?? null,
        });
      }
    }
    return Array.from(gruplar.values()).sort((a, b) => b.tutar - a.tutar);
  };

  /**
   * Bir masraf listesinin gelir ya da gider toplamını verir.
   * Eski kayıtlarda tek "Tutar" alanı yok; tutarlar aşamaya özel ayrı
   * sütunlarda duruyor, o yüzden iki sistem de ayrı ayrı ele alınıyor.
   */
  const listeToplami = (masraflar, isGelir) => {
    if (!Array.isArray(masraflar)) return 0;
    return masraflar.reduce((toplam, m) => {
      const tutar = al(m, 'Tutar');
      if (tutar != null) {
        return al(m, 'IsGelir') === isGelir ? toplam + (Number(tutar) || 0) : toplam;
      }
      if (isGelir) {
        return toplam
          + (Number(al(m, 'BasvuruAsamasindaGelenOdeme')) || 0)
          + (Number(al(m, 'TahkimAsamasindaGelenOdeme')) || 0)
          + (Number(al(m, 'MuvekkildenAlinanOdeme')) || 0);
      }
      return toplam
        + (Number(al(m, 'MuvekkileSigortaAsamasindaYapilanOdeme')) || 0)
        + (Number(al(m, 'MuvekkileTahkimAsamasindaYapilanOdeme')) || 0);
    }, 0);
  };

  if (!kasayaErisebilir) {
    return (
      <div className="kasa-container">
        <header className="kasa-header"><h1>Kasa</h1></header>
        <p className="kasa-bos">Bu sayfaya erişim yetkiniz bulunmamaktadır.</p>
      </div>
    );
  }

  const dosyaMasraflari = al(kasaData, 'DosyaMasraflari', []) || [];
  const ekMasraflar = al(kasaData, 'EkMasraflar', []) || [];
  const berileVerilenMasraflar = al(kasaData, 'BerileVerilenMasraflar', []) || [];

  const toplamGelir = Number(al(kasaData, 'ToplamGelir', 0)) || 0;
  const toplamGider = Number(al(kasaData, 'ToplamGider', 0)) || 0;
  const ofisBakiye = Number(al(kasaData, 'NetBakiye', 0)) || 0;

  const dosyaMasrafiToplam = Number(al(kasaData, 'DosyaMasrafiToplam', 0)) || 0;
  const muvekkileOdenen = Number(al(kasaData, 'MuvekkileOdenenToplam', 0)) || 0;
  const referansaVerilen = Number(al(kasaData, 'ReferansaVerilenToplam', 0)) || 0;
  const digerGider = Number(al(kasaData, 'DigerGiderToplam', 0)) || 0;
  const berileVerilen = Number(al(kasaData, 'BerileVerilenToplam', 0)) || 0;
  const baranaKalan = Number(al(kasaData, 'BaranaKalan', 0)) || 0;

  const dagilim = [
    { ad: 'Müvekkillere ödenen', tutar: muvekkileOdenen, sinif: 'muvekkile' },
    { ad: 'Dosya masrafları', tutar: dosyaMasrafiToplam, sinif: 'dosya' },
    { ad: 'Referanslara avans', tutar: referansaVerilen, sinif: 'referans' },
    { ad: 'Diğer giderler', tutar: digerGider, sinif: 'diger' },
  ].filter((x) => x.tutar > 0);

  // Backend'in ürettiği gider dökümü — dağılım toplamlarıyla aynı kaynaktan
  // geldiği için satırların toplamı üstteki tutarı her zaman tutturur.
  const giderDetaylari = (al(kasaData, 'GiderDetaylari', []) || []).map((d) => ({
    grup: al(d, 'Grup'),
    caseFileId: al(d, 'CaseFileId'),
    foyNo: al(d, 'FoyNo'),
    muvekkil: al(d, 'Muvekkil'),
    tutar: Number(al(d, 'Tutar', 0)) || 0,
    tarih: al(d, 'Tarih'),
  }));

  // Beril'e verilenler ayrı listede geliyor; aynı biçime çeviriyoruz.
  const berilSatirlari = berileVerilenMasraflar.map((m) => ({
    caseFileId: al(m, 'CaseFileId'),
    foyNo: al(m, 'FoyNo'),
    muvekkil: al(m, 'Muvekkil'),
    tutar: Number(al(m, 'Tutar', 0)) || 0,
    tarih: al(m, 'Tarih'),
  }));

  const dokumler = {
    muvekkile: dosyayaGoreTopla(giderDetaylari.filter((d) => d.grup === 'muvekkile')),
    dosya: dosyayaGoreTopla(giderDetaylari.filter((d) => d.grup === 'dosya')),
    referans: dosyayaGoreTopla(giderDetaylari.filter((d) => d.grup === 'referans')),
    diger: dosyayaGoreTopla(giderDetaylari.filter((d) => d.grup === 'diger')),
    beril: dosyayaGoreTopla(berilSatirlari),
  };

  /** Bir dağılım kaleminin dosya dökümünü çizer. */
  const dokumTablosu = (anahtar, toplam) => {
    const satirlar = dokumler[anahtar] || [];
    if (!satirlar.length) {
      return <p className="dokum-bos">Bu kalemde dosya bazlı kayıt bulunamadı.</p>;
    }
    return (
      <div className="dokum-sarmal">
        <table className="dokum-tablo">
          <thead>
            <tr>
              <th>Föy</th>
              <th>Müvekkil</th>
              <th className="sag">Kayıt</th>
              <th className="sag">Tutar</th>
              <th className="sag">Pay</th>
            </tr>
          </thead>
          <tbody>
            {satirlar.map((g) => (
              <tr key={g.anahtar}>
                <td className="sayisal">{g.foyNo ?? '—'}</td>
                <td>{g.muvekkil || (g.caseFileId ? '—' : 'Dosyasız / ofis')}</td>
                <td className="sag sayisal">{g.adet}</td>
                <td className="sag sayisal dokum-tutar">{paraFormat(g.tutar)}</td>
                <td className="sag sayisal dokum-pay">
                  {toplam > 0 ? '%' + ((g.tutar / toplam) * 100).toFixed(1).replace('.', ',') : '—'}
                </td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr>
              <td colSpan={2}>{satirlar.length} dosya</td>
              <td className="sag sayisal">
                {satirlar.reduce((t, g) => t + g.adet, 0)}
              </td>
              <td className="sag sayisal dokum-tutar">
                {paraFormat(satirlar.reduce((t, g) => t + g.tutar, 0))}
              </td>
              <td />
            </tr>
          </tfoot>
        </table>
      </div>
    );
  };

  const dosyaGelir = listeToplami(dosyaMasraflari, true);
  const dosyaGider = listeToplami(dosyaMasraflari, false);
  const ekGelir = listeToplami(ekMasraflar, true);
  const ekGider = listeToplami(ekMasraflar, false);

  /** Dosya masraflarını tabloda gösterilecek satırlara açar. */
  const dosyaSatirlari = dosyaMasraflari.flatMap((m) => {
    const id = al(m, 'Id');
    const tutar = al(m, 'Tutar');
    const satirlar = [];
    if (tutar != null) {
      satirlar.push({ tip: al(m, 'IsGelir') ? 'Gelir' : 'Gider', tutar, gelirMi: !!al(m, 'IsGelir') });
    } else {
      const ekle = (alan, tip, gelirMi) => {
        const v = al(m, alan);
        if (v != null) satirlar.push({ tip, tutar: v, gelirMi });
      };
      ekle('BasvuruAsamasindaGelenOdeme', 'Başvuru ödemesi', true);
      ekle('TahkimAsamasindaGelenOdeme', 'Tahkim ödemesi', true);
      ekle('MuvekkildenAlinanOdeme', 'Müvekkil ödemesi', true);
      ekle('MuvekkileSigortaAsamasindaYapilanOdeme', 'Sigorta gideri', false);
      ekle('MuvekkileTahkimAsamasindaYapilanOdeme', 'Tahkim gideri', false);
    }
    return satirlar.map((s, i) => ({
      key: id + '-' + i,
      foyNo: al(m, 'FoyNo') || '-',
      muvekkil: al(m, 'Muvekkil') || '-',
      tarih: al(m, 'Tarih'),
      masrafTuru: al(m, 'MasrafTuru') || '-',
      aciklama: al(m, 'Aciklama') || '-',
      ...s,
    }));
  }).filter((r) => r.gelirMi === (dosyaMasrafTab === 'gelir'));

  const sekmeler = [
    { id: 'dosya', ad: 'Dosya Masrafları', adet: dosyaMasraflari.length },
    { id: 'ek', ad: 'Ek Kayıtlar', adet: ekMasraflar.length },
    { id: 'beril', ad: "Beril'e Verilen", adet: berileVerilenMasraflar.length },
    { id: 'kdv', ad: 'KDV' },
  ];

  return (
    <div className="kasa-container">
      <header className="kasa-header">
        <div className="kasa-header-sol">
          <h1>Kasa</h1>
          <span className="kasa-donem">
            {AYLAR[selectedMonth - 1].label} {selectedYear}
          </span>
        </div>
        <div className="kasa-header-sag">
          <div className="kasa-month-selector">
            <select
              value={selectedMonth}
              onChange={(e) => setSelectedMonth(parseInt(e.target.value, 10))}
              className="form-control"
              aria-label="Ay"
            >
              {AYLAR.map((a) => <option key={a.value} value={a.value}>{a.label}</option>)}
            </select>
            <select
              value={selectedYear}
              onChange={(e) => setSelectedYear(parseInt(e.target.value, 10))}
              className="form-control"
              aria-label="Yıl"
            >
              {Array.from({ length: 10 }, (_, i) => new Date().getFullYear() - i)
                .map((y) => <option key={y} value={y}>{y}</option>)}
            </select>
          </div>
          {kisiselErisim && (
            <Link to="/kisisel-giderler" className="kasa-kisisel-link">
              Kişisel Giderler →
            </Link>
          )}
          <button type="button" className="btn btn-primary" onClick={() => setShowForm(true)}>
            + Kayıt Ekle
          </button>
        </div>
      </header>

      {loading ? (
        <p className="kasa-bos">Yükleniyor…</p>
      ) : (
        <>
          {/* ---- Ana özet ---- */}
          <section className="kasa-ozet">
            <article className="ozet-kart gelir">
              <span className="ozet-ad">Ofise Toplam Gelen</span>
              <strong className="ozet-tutar">{paraFormat(toplamGelir)}</strong>
            </article>
            <article className="ozet-kart gider">
              <span className="ozet-ad">Toplam Gider</span>
              <strong className="ozet-tutar">{paraFormat(toplamGider)}</strong>
            </article>
            <article className={'ozet-kart bakiye' + (ofisBakiye < 0 ? ' eksi' : '')}>
              <span className="ozet-ad">Ofis Toplam Kalan Bakiye</span>
              <strong className="ozet-tutar">{paraFormat(ofisBakiye)}</strong>
              <span className="ozet-alt">Gelen − Gider</span>
            </article>
          </section>

          {/* ---- Gider dağılımı ---- */}
          <section className="kasa-blok">
            <header className="blok-basi">
              <h2>Gider Dağılımı</h2>
              <span className="blok-toplam">{paraFormat(toplamGider)}</span>
            </header>

            {toplamGider > 0 ? (
              <>
                <div
                  className="dagilim-serit"
                  role="img"
                  aria-label={'Toplam gider ' + paraFormat(toplamGider)}
                >
                  {dagilim.map((d) => (
                    <span
                      key={d.sinif}
                      className={'dagilim-dilim ' + d.sinif}
                      style={{ width: (d.tutar / toplamGider) * 100 + '%' }}
                      title={d.ad + ': ' + paraFormat(d.tutar)}
                    />
                  ))}
                </div>
                <ul className="dagilim-liste">
                  {dagilim.map((d) => {
                    const acik = acikDokum === d.sinif;
                    return (
                      <li key={d.sinif} className={acik ? 'acik' : ''}>
                        <button
                          type="button"
                          className="dagilim-satir"
                          aria-expanded={acik}
                          onClick={() => setAcikDokum(acik ? null : d.sinif)}
                        >
                          <span className={'dagilim-ok' + (acik ? ' acik' : '')} aria-hidden="true" />
                          <span className={'dagilim-nokta ' + d.sinif} />
                          <span className="dagilim-ad">{d.ad}</span>
                          <span className="dagilim-oran">
                            %{((d.tutar / toplamGider) * 100).toFixed(1).replace('.', ',')}
                          </span>
                          <span className="dagilim-tutar">{paraFormat(d.tutar)}</span>
                        </button>
                        {acik && dokumTablosu(d.sinif, d.tutar)}
                      </li>
                    );
                  })}
                </ul>
              </>
            ) : (
              <p className="kasa-bos">Bu dönemde gider kaydı yok.</p>
            )}
          </section>

          {/* ---- Paylaşım ---- */}
          <section className="kasa-blok">
            <header className="blok-basi">
              <h2>Ofis Bakiyesinin Paylaşımı</h2>
            </header>
            <div className="paylasim">
              <div className="paylasim-satir">
                <span className="paylasim-ad">Ofis toplam kalan bakiye</span>
                <span className="paylasim-tutar">{paraFormat(ofisBakiye)}</span>
              </div>
              <div className={'paylasim-satir eksi' + (acikDokum === 'beril' ? ' acik' : '')}>
                <button
                  type="button"
                  className="paylasim-dugme"
                  aria-expanded={acikDokum === 'beril'}
                  onClick={() => setAcikDokum(acikDokum === 'beril' ? null : 'beril')}
                >
                  <span className={'dagilim-ok' + (acikDokum === 'beril' ? ' acik' : '')} aria-hidden="true" />
                  <span className="paylasim-ad">Beril'e verilen</span>
                  <span className="paylasim-tutar">− {paraFormat(berileVerilen)}</span>
                </button>
                {acikDokum === 'beril' && dokumTablosu('beril', berileVerilen)}
              </div>
              <div className={'paylasim-satir sonuc' + (baranaKalan < 0 ? ' negatif' : '')}>
                <span className="paylasim-ad">Baran'a kalan</span>
                <span className="paylasim-tutar">{paraFormat(baranaKalan)}</span>
              </div>
            </div>
          </section>

          {/* ---- Sekmeler ---- */}
          <nav className="kasa-sekmeler">
            {sekmeler.map((s) => (
              <button
                key={s.id}
                type="button"
                className={'kasa-sekme' + (sekme === s.id ? ' aktif' : '')}
                onClick={() => setSekme(s.id)}
              >
                {s.ad}
                {s.adet != null && <span className="kasa-sekme-adet">{s.adet}</span>}
              </button>
            ))}
          </nav>

          <section className="kasa-blok kasa-sekme-icerik">
            {sekme === 'dosya' && (
              <>
                <header className="blok-basi">
                  <h2>Dosya Masrafları</h2>
                  <span className="blok-ozet">
                    Gelir {paraFormat(dosyaGelir)} · Gider {paraFormat(dosyaGider)}
                  </span>
                </header>
                <div className="alt-sekmeler">
                  <button
                    type="button"
                    className={'alt-sekme' + (dosyaMasrafTab === 'gelir' ? ' aktif' : '')}
                    onClick={() => setDosyaMasrafTab('gelir')}
                  >Gelir</button>
                  <button
                    type="button"
                    className={'alt-sekme' + (dosyaMasrafTab === 'gider' ? ' aktif' : '')}
                    onClick={() => setDosyaMasrafTab('gider')}
                  >Gider</button>
                </div>
                <div className="kasa-table-wrap">
                  {dosyaSatirlari.length === 0 ? (
                    <p className="kasa-bos">Bu sekmede kayıt bulunmuyor.</p>
                  ) : (
                    <table className="kasa-table">
                      <thead>
                        <tr>
                          <th>Föy</th><th>Müvekkil</th><th className="sag">Tutar</th>
                          <th>Tarih</th><th>Tür</th><th>Açıklama</th>
                        </tr>
                      </thead>
                      <tbody>
                        {dosyaSatirlari.map((r) => (
                          <tr key={r.key}>
                            <td className="sayisal">{r.foyNo}</td>
                            <td>{r.muvekkil}</td>
                            <td className={'sag ' + (r.gelirMi ? 'gelir' : 'gider')}>
                              {paraFormat(r.tutar)}
                            </td>
                            <td className="sayisal">{formatDate(r.tarih)}</td>
                            <td>{r.masrafTuru}</td>
                            <td>{r.aciklama}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              </>
            )}

            {sekme === 'ek' && (
              <>
                <header className="blok-basi">
                  <h2>Ek Kayıtlar</h2>
                  <span className="blok-ozet">
                    Gelir {paraFormat(ekGelir)} · Gider {paraFormat(ekGider)}
                  </span>
                </header>
                <div className="kasa-table-wrap">
                  {ekMasraflar.length === 0 ? (
                    <p className="kasa-bos">Bu dönemde ek kayıt yok.</p>
                  ) : (
                    <table className="kasa-table">
                      <thead>
                        <tr>
                          <th className="sag">Tutar</th><th>Tarih</th>
                          <th>Tür</th><th>Açıklama</th><th className="kasa-table-actions" />
                        </tr>
                      </thead>
                      <tbody>
                        {ekMasraflar.map((m) => {
                          const id = al(m, 'Id');
                          return (
                            <tr key={id}>
                              <td className={'sag ' + (al(m, 'IsGelir') ? 'gelir' : 'gider')}>
                                {paraFormat(al(m, 'Tutar', 0))}
                              </td>
                              <td className="sayisal">{formatDate(al(m, 'Tarih'))}</td>
                              <td>{al(m, 'MasrafTuru') || '-'}</td>
                              <td>{al(m, 'Aciklama') || '-'}</td>
                              <td className="kasa-table-actions">
                                <button type="button" className="btn btn-danger btn-sm"
                                  onClick={() => handleDelete(id)}>Sil</button>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  )}
                </div>
              </>
            )}

            {sekme === 'beril' && (
              <>
                <header className="blok-basi">
                  <h2>Beril'e Verilen</h2>
                  <span className="blok-toplam">{paraFormat(berileVerilen)}</span>
                </header>
                <div className="kasa-table-wrap">
                  {berileVerilenMasraflar.length === 0 ? (
                    <p className="kasa-bos">Bu dönemde kayıt yok.</p>
                  ) : (
                    <table className="kasa-table">
                      <thead>
                        <tr>
                          <th>Föy</th><th>Müvekkil</th><th>Tarih</th>
                          <th>Açıklama</th><th className="sag">Tutar</th>
                          <th className="kasa-table-actions" />
                        </tr>
                      </thead>
                      <tbody>
                        {berileVerilenMasraflar.map((m) => {
                          const id = al(m, 'Id');
                          return (
                            <tr key={id}>
                              <td className="sayisal">{al(m, 'FoyNo') || '-'}</td>
                              <td>{al(m, 'Muvekkil') || '-'}</td>
                              <td className="sayisal">{formatDate(al(m, 'Tarih'))}</td>
                              <td>{al(m, 'Aciklama') || '-'}</td>
                              <td className="sag gider">{paraFormat(al(m, 'Tutar', 0))}</td>
                              <td className="kasa-table-actions">
                                <button type="button" className="btn btn-danger btn-sm"
                                  onClick={() => handleDelete(id)}>Sil</button>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  )}
                </div>
              </>
            )}

            {sekme === 'kdv' && (
              <>
                <header className="blok-basi">
                  <h2>Makbuzlardan Doğan KDV</h2>
                </header>
                <KdvPaneli yil={selectedYear} ay={selectedMonth} />
              </>
            )}

          </section>
        </>
      )}

      {showForm && (
        <div className="kasa-perde" onClick={() => setShowForm(false)}>
          <form className="kasa-pencere" onClick={(e) => e.stopPropagation()} onSubmit={handleSubmit}>
            <h3>Kasaya Kayıt Ekle</h3>

            <div className="kasa-tip-secim">
              <button
                type="button"
                className={'tip-dugme' + (formData.IsGelir ? ' aktif gelir' : '')}
                onClick={() => setFormData({ ...formData, IsGelir: true, IsBerileVerilen: false })}
              >Gelir</button>
              <button
                type="button"
                className={'tip-dugme' + (!formData.IsGelir ? ' aktif gider' : '')}
                onClick={() => setFormData({ ...formData, IsGelir: false })}
              >Gider</button>
            </div>

            {!formData.IsGelir && (
              <label className="kasa-onay">
                <input
                  type="checkbox"
                  checked={formData.IsBerileVerilen}
                  onChange={(e) => setFormData({ ...formData, IsBerileVerilen: e.target.checked })}
                />
                Beril'e verilen
              </label>
            )}

            <label>
              Tutar (₺)
              <input
                type="number" step="0.01" inputMode="decimal" className="form-control"
                value={formData.Tutar}
                onChange={(e) => setFormData({ ...formData, Tutar: e.target.value })}
                required autoFocus
              />
            </label>

            <label>
              Tarih
              <input
                type="date" className="form-control"
                value={formData.MasrafIstenmeTarihi}
                onChange={(e) => setFormData({ ...formData, MasrafIstenmeTarihi: e.target.value })}
                required
              />
            </label>

            <label>
              Tür
              <input
                type="text" className="form-control"
                value={formData.MasrafTuru}
                onChange={(e) => setFormData({ ...formData, MasrafTuru: e.target.value })}
                placeholder="Örn: OFİS KİRA"
              />
            </label>

            <label>
              Açıklama
              <input
                type="text" className="form-control"
                value={formData.Aciklama}
                onChange={(e) => setFormData({ ...formData, Aciklama: e.target.value })}
              />
            </label>

            <div className="kasa-pencere-dugmeler">
              <button type="button" className="btn btn-secondary" onClick={() => setShowForm(false)}>
                Vazgeç
              </button>
              <button type="submit" className="btn btn-primary">Kaydet</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
};

export default Kasa;
